/**
 * useResizableWidth — 드래그로 폭을 조절하는 사이드바/패널의 공용 상태 훅.
 *
 * 폭은 px 단위로 localStorage에 기억한다(테마·lastWorkspace처럼 브라우저 단위
 * 저장 — 계정 단위 아님). 저장값이 없거나 접근 불가(프라이버시 모드 등)면
 * defaultWidth로 시작한다. 핸들에는 반환된 handleProps를 그대로 배선한다
 * (WidthResizeHandle 참조); 더블클릭·Enter는 기본 폭 리셋이다.
 *
 * 드래그 중에는 dragging이 true가 된다 — 소비처는 이때 width transition을
 * 꺼야 핸들이 포인터를 실시간 추종한다.
 *
 * 폭은 minWidth까지 연속으로 줄어든다 — 내용이 잘려 보이는 좁은 폭도
 * 사용자가 알고 선택한 것이므로 그대로 렌더·저장한다. onCollapse를 주면
 * minWidth가 곧 스냅 임계값이 되어, 그 미만으로 끌면 완전 접힘으로
 * 전환된다(다시 펼치면 드래그 직전 폭 복원).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';

interface ResizableWidthOptions {
  /** localStorage 키 (`vsmr.*` 관례) — 사용처마다 고유해야 한다 */
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /**
   * 핸들이 붙는 가장자리. 'right' = 좌측 사이드바(오른쪽으로 끌면 넓어짐),
   * 'left' = 우측 패널(왼쪽으로 끌면 넓어짐).
   */
  handleEdge: 'left' | 'right';
  /**
   * minWidth 미만으로 끌면 호출 — 소비처가 완전 접힘으로 전환한다.
   * 폭은 드래그 시작값으로 복원해 두므로 다시 펼치면 직전 폭이 나온다.
   * 미지정 시 minWidth에서 멈춘다.
   */
  onCollapse?: () => void;
}

/** 키보드(화살표) 리사이즈 1회 이동량 */
const KEYBOARD_STEP = 16;

function readStoredWidth(key: string, min: number, max: number): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    return Math.min(max, Math.max(min, value));
  } catch {
    return null;
  }
}

function storeWidth(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(Math.round(value)));
  } catch {
    // 저장 실패는 무해 — 이번 세션 동안만 유지될 뿐
  }
}

/** 핸들 요소에 스프레드할 이벤트 배선의 타입 (WidthResizeHandle가 받는다) */
export type ResizeHandleWiring = ReturnType<typeof useResizableWidth>['handleProps'];

export function useResizableWidth({
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth,
  handleEdge,
  onCollapse,
}: ResizableWidthOptions) {
  const [width, setWidth] = useState(
    () => readStoredWidth(storageKey, minWidth, maxWidth) ?? defaultWidth,
  );
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{ x: number; width: number } | null>(null);

  const clamp = useCallback(
    (value: number) => Math.min(maxWidth, Math.max(minWidth, value)),
    [minWidth, maxWidth],
  );
  // 상한은 호출부의 레이아웃에 따라 변할 수 있다(남는 자리). 저장 폭은 그대로 두고 **유효 폭**만
  // 잘라 낸다 — 자리가 돌아오면 저장 폭이 되살아난다. 드래그 시작점·키보드·aria 는 유효 폭이
  // 기준이다(저장 폭 기준이면 잘린 만큼의 데드존이 생긴다).
  const effectiveWidth = clamp(width);

  // 포인터가 핸들을 벗어나도 커서·텍스트 선택이 흔들리지 않게 드래그 동안 고정
  useEffect(() => {
    if (!dragging) return;
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [dragging]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      // 주 버튼(좌클릭·터치)만 — 우클릭 컨텍스트 메뉴 등 기본 동작 보존
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragStart.current = { x: e.clientX, width: effectiveWidth };
      setDragging(true);
    },
    [effectiveWidth],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!dragStart.current) return;
      const delta = e.clientX - dragStart.current.x;
      const raw = dragStart.current.width + (handleEdge === 'right' ? delta : -delta);
      if (onCollapse && raw < minWidth) {
        // 스냅 접힘 — 드래그를 여기서 끝내고, 다음 펼침을 위해 시작 폭을 복원·저장.
        // 핸들이 언마운트되기 전에 캡처도 명시 해제해 커서/이벤트 잔류를 막는다.
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
        const startWidth = dragStart.current.width;
        dragStart.current = null;
        setDragging(false);
        setWidth(startWidth);
        storeWidth(storageKey, startWidth);
        onCollapse();
        return;
      }
      setWidth(clamp(raw));
    },
    [clamp, handleEdge, minWidth, onCollapse, storageKey],
  );

  const onPointerUp = useCallback(() => {
    if (!dragStart.current) return;
    dragStart.current = null;
    setDragging(false);
    setWidth((w) => {
      storeWidth(storageKey, w);
      return w;
    });
  }, [storageKey]);

  const reset = useCallback(() => {
    setWidth(defaultWidth);
    storeWidth(storageKey, defaultWidth);
  }, [defaultWidth, storageKey]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter') {
        reset();
        return;
      }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const towardWider =
        (handleEdge === 'right') === (e.key === 'ArrowRight') ? KEYBOARD_STEP : -KEYBOARD_STEP;
      setWidth((w) => {
        const next = clamp(clamp(w) + towardWider);
        storeWidth(storageKey, next);
        return next;
      });
    },
    [clamp, handleEdge, reset, storageKey],
  );

  return {
    width: effectiveWidth,
    dragging,
    /** WidthResizeHandle(또는 임의 핸들 요소)에 스프레드할 이벤트·ARIA 배선 */
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onDoubleClick: reset,
      onKeyDown,
      // 포커스 가능한 separator — 스크린 리더가 현재 폭/범위를 읽도록
      'aria-valuemin': minWidth,
      'aria-valuemax': maxWidth,
      'aria-valuenow': Math.round(effectiveWidth),
    },
  };
}
